import React from 'react';
import {
  BooleanField,
  Button,
  Datagrid,
  DateField,
  DeleteButton,
  EditButton,
  FunctionField,
  List,
  ReferenceField,
  ShowButton,
  TextField,
  useDataProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import { DATE_FORMATER, PERMISSIONS } from '../../constants';
import { buildSavedMessage } from '../../components/FormRecurringSend/nextSend';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const frequencyLabels = {
  daily: 'Diária',
  weekly: 'Semanal',
};

const ToggleActiveButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // O id já está embutido no path do endpoint customizado /toggle.
      // Passamos id: '' pra evitar que o update.ts concatene /{id} no final
      // e isActive no data pra escapar do short-circuit que cancela quando data === {}.
      const resource = `chains/chainId/recurring-send/${record.id}/toggle`;
      const response: any = await dataProvider.update(resource, {
        id: '',
        data: { isActive: !record.isActive },
        previousData: record,
      });
      if (record.isActive) {
        notify('Envio pausado', { type: 'success' });
      } else {
        const nextSendMessage = buildSavedMessage({ ...record, isActive: true }, response?.data?.todaySendAt);
        notify(nextSendMessage ? `Envio retomado. ${nextSendMessage}` : 'Envio retomado', { type: 'success', autoHideDuration: 6000 });
      }
      refresh();
    } catch (error) {
      notify('Erro ao alterar status', { type: 'error' });
    }
  };

  return (
    <Button
      label={record.isActive ? 'Pausar' : 'Retomar'}
      onClick={handleToggle}
    >
      {record.isActive ? <PauseIcon /> : <PlayArrowIcon />}
    </Button>
  );
};

const ListRecurringSend: React.FC = (props) => {
  return (
    <Permission permission={PERMISSIONS.UNIFIED_SEND}>
      <List
        {...props}
        perPage={25}
        pagination={<ListPagination />}
        exporter={false}
        title="Envios Recorrentes"
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="title" label="Título" />
          <FunctionField
            source="frequency"
            label="Frequência"
            render={(record) => frequencyLabels[record?.frequency] || record?.frequency}
          />
          <FunctionField
            source="isActive"
            label="Status"
            render={(record) => record?.isActive ? 'Ativo' : 'Pausado'}
          />
          <DateField source="startAt" label="Data Início" options={DATE_FORMATER} />
          <DateField source="endAt" label="Data Fim" options={DATE_FORMATER} emptyText="-" />
          <BooleanField source="pushNotification" label="Push" />
          <BooleanField source="sms" label="SMS" />
          <ReferenceField source="customerTagId" reference="chains/chainId/tag" label="Etiqueta (clientes)" link={false}>
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="stationTagId" reference="chains/chainId/tag" label="Etiqueta (postos)" link={false}>
            <TextField source="name" />
          </ReferenceField>
          <ShowButton />
          <EditButton />
          <ToggleActiveButton />
          <DeleteButton />
        </Datagrid>
      </List>
    </Permission>
  );
};

export default ListRecurringSend;
